import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LineChart } from 'lucide-react';

/**
 * Etiqueta "Histórico" que despliega la evolución del precio.
 *
 * POR QUÉ EXISTE
 * Antes había dos leyendas que afirmaban una conclusión: "Precio mínimo
 * histórico" y "Mínimo hist.: $X · hace N días". El problema es la base que las
 * sostenía: sobre los datos reales, la enorme mayoría de los productos tiene
 * una o dos observaciones de precio, y con dos puntos afirmar "mínimo
 * histórico" es prematuro. Peor: cuando un dato viene sucio —hay productos
 * cuyo "mínimo" es ocho veces menor que su precio actual— la leyenda lo
 * presentaba como un hecho comprobado.
 *
 * La etiqueta ahora es neutra y el gráfico deja que el usuario juzgue: si el
 * precio se sostiene, si salta, y sobre cuántas mediciones se apoya.
 *
 * DECISIONES DE VISUALIZACIÓN
 * - Línea (evolución en el tiempo), una sola serie: sin leyenda, el título la nombra.
 * - Tratamiento de énfasis: la línea va en gris de contexto y el punto que
 *   importa —el mínimo— en el acento de la marca.
 * - Etiquetas directas solo en el mínimo y el precio de hoy. Un número sobre
 *   cada punto sería ruido en un gráfico de este tamaño.
 * - Se muestra el conteo de registros: es lo que permite calibrar cuánta
 *   confianza merece la serie, y es justo lo que las leyendas viejas ocultaban.
 */

// Emphasis: gris de contexto para la línea, acento para el punto que importa.
const COLOR_LINEA  = '#64748b';
const COLOR_MINIMO = '#FF4500';
const COLOR_TINTA  = '#1f2937';
const COLOR_SUAVE  = '#6b7280';

const ANCHO = 240;
const ALTO  = 64;
const PAD_X = 10;
const PAD_Y = 10;

const ANCHO_POPUP = 264;
const ALTO_POPUP  = 168;

const fmtPrecio = (n, moneda = 'ARS') => {
  if (n == null) return '';
  const simbolo = moneda === 'EUR' ? '€' : '$';
  return `${simbolo}${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const diasDesde = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

/** Sparkline de la serie, con el mínimo marcado. */
const Grafico = ({ puntos, moneda }) => {
  const precios = puntos.map(p => p.precio);
  const min = Math.min(...precios);
  const max = Math.max(...precios);
  // Si todos los precios son iguales se dibuja una recta al medio, en vez de
  // dividir por cero.
  const rango = max - min || 1;

  const x = (i) => (puntos.length === 1
    ? ANCHO / 2
    : PAD_X + (i * (ANCHO - PAD_X * 2)) / (puntos.length - 1));
  const y = (v) => (max === min
    ? ALTO / 2
    : PAD_Y + (ALTO - PAD_Y * 2) * (1 - (v - min) / rango));

  const trazo = puntos
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.precio).toFixed(1)}`)
    .join(' ');

  const iMin    = precios.indexOf(min);
  const iUltimo = puntos.length - 1;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      role="img"
      aria-label={`Evolución del precio en ${puntos.length} registros. Valor más bajo ${fmtPrecio(min, moneda)}.`}
      style={{ display: 'block' }}
    >
      {/* Base hairline: da referencia de suelo sin competir con la línea */}
      <line x1="0" y1={ALTO - 0.5} x2={ANCHO} y2={ALTO - 0.5} stroke="#e5e7eb" strokeWidth="1" />

      <path
        d={trazo}
        fill="none"
        stroke={COLOR_LINEA}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Puntos de contexto, discretos */}
      {puntos.map((p, i) => (
        i === iMin ? null : (
          <circle key={i} cx={x(i)} cy={y(p.precio)} r="2.5" fill={COLOR_LINEA} opacity="0.55" />
        )
      ))}

      {/* Precio de hoy: anillo de superficie para leerse sobre la línea */}
      {iUltimo !== iMin && (
        <circle
          cx={x(iUltimo)}
          cy={y(puntos[iUltimo].precio)}
          r="4"
          fill={COLOR_LINEA}
          stroke="#fff"
          strokeWidth="2"
        />
      )}

      {/* El mínimo: el punto que el usuario vino a ver */}
      <circle cx={x(iMin)} cy={y(min)} r="5" fill={COLOR_MINIMO} stroke="#fff" strokeWidth="2" />
    </svg>
  );
};

export const HistorialPrecios = ({ serie, moneda = 'ARS', idPrefix = 'historial' }) => {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);
  const anclaRef = useRef(null);

  const ubicar = useCallback(() => {
    const el = anclaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Se ancla a la etiqueta pero sin salirse de la pantalla: en móvil la card
    // puede estar pegada al borde derecho.
    const left = Math.min(Math.max(8, r.left), window.innerWidth - ANCHO_POPUP - 8);
    const entraAbajo = r.bottom + ALTO_POPUP + 8 < window.innerHeight;
    setPos({
      left,
      top: entraAbajo ? r.bottom + 8 : Math.max(8, r.top - ALTO_POPUP - 8),
    });
  }, []);

  useEffect(() => {
    if (!abierto) return undefined;
    ubicar();
    // Al hacer scroll el ancla se mueve: cerrar es más honesto que dejar el
    // popup flotando donde ya no corresponde.
    const cerrar = () => setAbierto(false);
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto, ubicar]);

  // Sin datos suficientes no se muestra nada: una serie de un solo punto no
  // dice si el precio subió o bajó, y prometer "histórico" sería vacío.
  if (!serie || !Array.isArray(serie.puntos) || serie.puntos.length < 2) return null;

  const puntos   = serie.puntos;
  const total    = serie.total || puntos.length;
  const precios  = puntos.map(p => p.precio);
  const min      = Math.min(...precios);
  const actual   = puntos[puntos.length - 1].precio;
  const minPunto = puntos.find(p => p.precio === min);
  const dias     = diasDesde(minPunto && minPunto.fecha);
  const enMinimo = actual <= min + 0.01;

  const abrir  = () => setAbierto(true);
  const cerrar = () => setAbierto(false);

  return (
    <>
      <button
        ref={anclaRef}
        id={idPrefix}
        type="button"
        // El estilo vive en `.boton-historico` (index.css); `tap-exempt` evita
        // que el mínimo táctil global de 44px estire la píldora dentro de la card.
        className="boton-historico tap-exempt"
        aria-expanded={abierto}
        aria-label="Ver evolución del precio"
        onMouseEnter={abrir}
        onMouseLeave={cerrar}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAbierto(v => !v); }}
      >
        <LineChart size={13} />
        Histórico
      </button>

      {abierto && pos && createPortal(
        <div
          id={`${idPrefix}-popup`}
          onMouseEnter={abrir}
          onMouseLeave={cerrar}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, width: ANCHO_POPUP,
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.16)', padding: '10px 12px 11px',
            zIndex: 4000,
          }}
        >
          <div style={{
            fontSize: '0.7rem', fontWeight: 700, color: COLOR_TINTA,
            marginBottom: 6, display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', gap: 8,
          }}>
            <span>Evolución del precio</span>
            <span style={{ fontSize: '0.62rem', fontWeight: 600, color: COLOR_SUAVE }}>
              {total} {total === 1 ? 'registro' : 'registros'}
            </span>
          </div>

          <Grafico puntos={puntos} moneda={moneda} />

          {/* Etiqueta directa: solo el mínimo */}
          <div style={{ marginTop: 7, fontSize: '0.66rem', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: COLOR_MINIMO, flexShrink: 0,
            }} />
            <span style={{ color: COLOR_SUAVE }}>
              Más bajo{' '}
              <strong style={{ color: COLOR_TINTA, fontWeight: 700 }}>
                {fmtPrecio(min, moneda)}
              </strong>
              {dias != null && dias > 0 ? ` · hace ${dias}d` : ''}
            </span>
          </div>

          <div style={{
            marginTop: 6, paddingTop: 6, borderTop: '1px solid #f1f5f9',
            fontSize: '0.62rem', color: COLOR_SUAVE, lineHeight: 1.45,
          }}>
            {enMinimo
              ? 'Hoy está en el valor más bajo que registramos.'
              : `Hoy: ${fmtPrecio(actual, moneda)}.`}
            {total < 3 ? ' Pocos registros todavía: la serie recién empieza.' : ''}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
